import { useState, useEffect, createContext, useContext, useRef, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import ForceGraph2D from 'react-force-graph-2d';

// Imported Page Components (Refactored)
import { DashboardPage } from "./pages/dashboard";
import { 
  EmployeePortalPage as PortalPage, 
  VacationsPage as PortalVacationsPage, 
  AbsencesPage as PortalAbsencesPage, 
  TrainingsPage as PortalTrainingsPage, 
  VehiclesPage as PortalVehiclesPage, 
  BenefitsPage as PortalBenefitsPage,
  EmployeeSelfServicePortal
} from "./pages/portal";
import { 
  ClientsPage, 
  SuppliersPage, 
  EmployeesPage,
  ClientDetailPage,
  SupplierDetailPage,
  EmployeeDetailPage,
  OthersPage,
  OtherDetailPage
} from "./pages/entities";
import ImportedConflictsPage from "./pages/ConflictsPage";
import ImportedRelationshipGraphPage from "./pages/RelationshipGraphPage";
import ImportedOffersPage from "./pages/OffersPage";
import ImportedSettingsPage from "./pages/SettingsPage";
import ImportedDocumentsPage from "./pages/DocumentsPage";
import ImportedReportsPage from "./pages/ReportsPage";
import ImportedComplianceDashboardPage from "./pages/ComplianceDashboardPage";
import ImportedUsersPage from "./pages/UsersPage";
import ImportedTenantsPage from "./pages/TenantsPage";
import ImportedAuditLogPage from "./pages/AuditLogPage";
import {
  HRDashboardPage,
  AttendancePage,
  HRAbsencesPage,
  RecruitmentPage,
  LifecyclePage,
  ITTicketsPage,
  PerformanceReviewsPage,
  EquipmentPage,
  HRVehiclesPage,
  HRTrainingsPage,
  HRBenefitsPage,
  FinesPage,
  KmMapsPage,
  KmApprovalPage,
  KmDashboardPage
} from "./pages/hr";
import { translateStatus, translateRisk } from "./lib/constants";

// Context
const AuthContext = createContext(null);
const ThemeContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API instance
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Theme Provider
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await api.get('/auth/session', {
          headers: { 'X-Session-ID': sessionId }
        });
        setUser(response.data);
        navigate('/dashboard', { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Falha na autenticação');
        navigate('/login');
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">A autenticar...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Role-based Route Protection
const RestrictedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If colaborador tries to access restricted routes, redirect to self-service
  if (user.role === 'colaborador' && !allowedRoles.includes('colaborador')) {
    return <Navigate to="/self-service" replace />;
  }

  // Check if user's role is in allowed roles (if specified)
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/self-service" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL hash (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'hr_manager', 'user', 'viewer']}><DashboardLayout><DashboardPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/clients" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><ClientsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/clients/:id" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><ClientDetailPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/suppliers" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><SuppliersPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/suppliers/:id" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><SupplierDetailPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/employees" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'hr_manager', 'user', 'viewer']}><DashboardLayout><EmployeesPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/employees/:id" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'hr_manager', 'user', 'viewer']}><DashboardLayout><EmployeeDetailPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/others" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><OthersPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/others/:id" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><OtherDetailPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/conflicts" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><ImportedConflictsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/graph" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><ImportedRelationshipGraphPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/offers" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><ImportedOffersPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/documents" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><ImportedDocumentsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/reports" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer', 'user', 'viewer']}><DashboardLayout><ImportedReportsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/compliance-dashboard" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer']}><DashboardLayout><ImportedComplianceDashboardPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/portal" element={<ProtectedRoute><DashboardLayout><PortalPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/portal/vacations" element={<ProtectedRoute><DashboardLayout><PortalVacationsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/portal/absences" element={<ProtectedRoute><DashboardLayout><PortalAbsencesPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/portal/trainings" element={<ProtectedRoute><DashboardLayout><PortalTrainingsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/portal/vehicles" element={<ProtectedRoute><DashboardLayout><PortalVehiclesPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/portal/benefits" element={<ProtectedRoute><DashboardLayout><PortalBenefitsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/tenants" element={<RestrictedRoute allowedRoles={['super_admin']}><DashboardLayout><ImportedTenantsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/audit-log" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'compliance_officer']}><DashboardLayout><ImportedAuditLogPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><HRDashboardPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/attendance" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><AttendancePage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/absences" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><HRAbsencesPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/recruitment" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><RecruitmentPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/lifecycle" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><LifecyclePage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/tickets" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><ITTicketsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/reviews" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><PerformanceReviewsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/equipment" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><EquipmentPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/vehicles" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><HRVehiclesPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/fines" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><FinesPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/km-maps" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><KmMapsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/km-approval" element={<DashboardLayout><KmApprovalPage /></DashboardLayout>} />
      <Route path="/hr/km-dashboard" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><KmDashboardPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/trainings" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><HRTrainingsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/hr/benefits" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin', 'hr_manager']}><DashboardLayout><HRBenefitsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/settings" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin']}><DashboardLayout><ImportedSettingsPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/users" element={<RestrictedRoute allowedRoles={['super_admin', 'tenant_admin']}><DashboardLayout><ImportedUsersPage /></DashboardLayout></RestrictedRoute>} />
      <Route path="/self-service" element={<ProtectedRoute><EmployeeSelfServicePortal /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Landing Page
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    // Redirect colaborador role to self-service portal
    if (user.role === 'colaborador') {
      return <Navigate to="/self-service" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/1ed57a29-834c-4440-94bb-8fa514a38873/images/893d9cb746463cc17aacfefa048bcc640ffd2f5ff4ab77b7e5239f606122dde9.png" 
              alt="CogniBiz Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-bold text-xl font-['Plus_Jakarta_Sans']">CogniBiz</span>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              data-testid="login-btn"
            >
              Entrar
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Plus_Jakarta_Sans'] tracking-tight mb-6">
              <span className="text-gradient">CogniBiz</span>
              <span className="block text-2xl md:text-3xl lg:text-4xl mt-2 text-muted-foreground font-normal">Know your Business</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
              Plataforma empresarial para gestão de KYC, KYS e KYE, com deteção automática de conflitos, conformidade RGPC, análise de risco potenciada por IA e funcionalidades integradas de gestão de Recursos Humanos.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
                data-testid="get-started-btn"
              >
                Começar Agora
              </button>
              <button
                onClick={() => navigate('/login?redirect=/self-service')}
                className="px-6 py-3 rounded-lg border border-border bg-card font-semibold hover:bg-muted transition-colors"
                data-testid="self-service-btn"
              >
                Portal do Colaborador
              </button>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 border border-border shadow-2xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/1ed57a29-834c-4440-94bb-8fa514a38873/images/90b593b5fbe50f286ef781923593e99a60c4cc8860c9c76e0486854bc9dcf3a8.png"
                alt="Business Compliance Intelligence"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold font-['Plus_Jakarta_Sans'] text-center mb-4">
            Funcionalidades Principais
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Tudo o que precisa para gerir conflitos de interesse e garantir conformidade regulatória
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: '👥', title: 'KYC - Know Your Client', desc: 'Gestão completa de clientes com verificação de identidade e análise de risco' },
              { icon: '🏢', title: 'KYS - Know Your Supplier', desc: 'Controlo de fornecedores com due diligence e monitorização contínua' },
              { icon: '👤', title: 'KYE - Know Your Employee', desc: 'Gestão de colaboradores com declarações de conflito e histórico de ofertas' },
              { icon: '⚠️', title: 'Deteção de Conflitos', desc: 'Motor de IA para cruzamento de dados e identificação automática de conflitos' },
              { icon: '📄', title: 'Gestão Documental', desc: 'Centralização de documentos com versionamento e alertas de expiração' },
              { icon: '🔒', title: 'Conformidade RGPC', desc: 'Gestão de Riscos, Ofertas, Planos e Códigos de Ética em conformidade regulatória' },
              { icon: '👨‍💼', title: 'Gestão de RH', desc: 'Portal completo de Recursos Humanos com férias, ausências, formações e benefícios' },
              { icon: '📊', title: 'Análise de Risco IA', desc: 'Análise avançada potenciada por inteligência artificial com scoring automático' },
            ].map((feature, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-card border border-border hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold font-['Plus_Jakarta_Sans'] mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/1ed57a29-834c-4440-94bb-8fa514a38873/images/893d9cb746463cc17aacfefa048bcc640ffd2f5ff4ab77b7e5239f606122dde9.png" 
              alt="CogniBiz Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-semibold">CogniBiz - Know your Business</span>
          </div>
          <p className="text-sm text-muted-foreground">Designed by <a href="https://lptec.pt" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">LPtec.pt</a> and Powered by <a href="https://cogniseg.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">Cogniseg.com</a></p>
        </div>
      </footer>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const { user, login, setUser } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (user) {
    // Redirect colaborador role to self-service portal
    if (user.role === 'colaborador') {
      return <Navigate to="/self-service" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleLocalAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister 
        ? { email: formData.email, password: formData.password, name: formData.name }
        : { email: formData.email, password: formData.password };
      
      const response = await api.post(endpoint, payload);
      setUser(response.data);
      
      // Redirect based on role
      if (response.data.role === 'colaborador') {
        navigate('/self-service', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
      toast.success(isRegister ? 'Registo efetuado com sucesso!' : 'Login efetuado com sucesso!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Ocorreu um erro. Tente novamente.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-primary">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/1ed57a29-834c-4440-94bb-8fa514a38873/images/90b593b5fbe50f286ef781923593e99a60c4cc8860c9c76e0486854bc9dcf3a8.png"
          alt="CogniBiz Platform"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <h2 className="text-4xl font-bold text-white font-['Plus_Jakarta_Sans'] mb-4">
            Proteja a integridade da sua organização
          </h2>
          <p className="text-white/80 text-lg">
            Gestão Proativa de Conformidade em tempo real.<br />CogniBiz - Know your Business
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/1ed57a29-834c-4440-94bb-8fa514a38873/images/893d9cb746463cc17aacfefa048bcc640ffd2f5ff4ab77b7e5239f606122dde9.png" 
              alt="CogniBiz Logo" 
              className="w-12 h-12 object-contain"
            />
            <span className="font-bold text-2xl font-['Plus_Jakarta_Sans']">CogniBiz</span>
          </div>

          <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-2">
            {isRegister ? 'Criar conta' : 'Bem-vindo de volta'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {isRegister ? 'Preencha os dados para criar a sua conta' : 'Faça login para aceder à sua conta'}
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Local Auth Form */}
          <form onSubmit={handleLocalAuth} className="space-y-4 mb-6">
            {isRegister && (
              <div>
                <label className="block text-sm font-medium mb-1">Nome</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="O seu nome"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="exemplo@empresa.pt"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
                minLength={6}
                data-testid="login-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
                  {isRegister ? 'A criar conta...' : 'A entrar...'}
                </span>
              ) : (
                isRegister ? 'Criar Conta' : 'Entrar'
              )}
            </button>
          </form>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">ou</span>
            </div>
          </div>

          {/* Google Auth */}
          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-lg border border-border hover:bg-muted transition-colors font-medium"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar com Google
          </button>

          {/* Toggle Register/Login */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {isRegister ? 'Já tem uma conta?' : 'Não tem uma conta?'}{' '}
              <button
                type="button"
                onClick={() => {
                  setIsRegister(!isRegister);
                  setError('');
                  setFormData({ email: '', password: '', name: '' });
                }}
                className="text-primary hover:underline font-medium"
                data-testid="toggle-auth-mode-btn"
              >
                {isRegister ? 'Fazer login' : 'Criar conta'}
              </button>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              Ao continuar, concorda com os nossos{' '}
              <a href="#" className="text-primary hover:underline">Termos de Serviço</a>{' '}
              e{' '}
              <a href="#" className="text-primary hover:underline">Política de Privacidade</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Theme Toggle
const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg hover:bg-muted transition-colors"
      data-testid="theme-toggle"
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

// Dashboard Layout
const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications?unread_only=true');
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/self-service', label: 'Portal Self-Service', icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
    { 
      path: '/hr', 
      label: 'Gestão de RH', 
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      submenu: [
        { path: '/hr', label: 'Dashboard RH' },
        { path: '/hr/attendance', label: 'Assiduidade' },
        { path: '/hr/absences', label: 'Ausências' },
        { path: '/hr/trainings', label: 'Formações' },
        { path: '/hr/benefits', label: 'Benefícios' },
        { 
          path: '/hr/vehicles', 
          label: 'Viaturas',
          submenu: [
            { path: '/hr/vehicles', label: 'Gestão Viaturas' },
            { path: '/hr/km-maps', label: 'Mapas KM' },
            { path: '/hr/km-approval', label: 'Aprovação KM' },
            { path: '/hr/km-dashboard', label: 'Dashboard KM' },
            { path: '/hr/fines', label: 'Coimas' }
          ]
        },
        { path: '/hr/lifecycle', label: 'Onboarding/Offboarding' },
        { path: '/hr/equipment', label: 'Equipamentos IT' },
        { path: '/hr/tickets', label: 'Tickets IT' },
        { path: '/hr/recruitment', label: 'Recrutamento' },
        { path: '/hr/reviews', label: 'Avaliações' }
      ]
    },
    { path: '/clients', label: 'Clientes (KYC)', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { path: '/suppliers', label: 'Fornecedores (KYS)', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { path: '/employees', label: 'Colaboradores (KYE)', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { path: '/others', label: 'Outros (KYU)', icon: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9' },
    { 
      path: '/conflicts', 
      label: 'Conflitos', 
      icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      submenu: [
        { path: '/conflicts', label: 'Lista de Conflitos' },
        { path: '/graph', label: 'Grafo de Relacionamentos' }
      ]
    },
    { path: '/offers', label: 'Ofertas', icon: 'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7' },
    { path: '/documents', label: 'Documentos', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { path: '/reports', label: 'Relatórios', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { path: '/compliance-dashboard', label: 'Dashboard Compliance', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { path: '/users', label: 'Utilizadores', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { path: '/settings', label: 'Configurações', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
  ];

  // State for expanded submenus
  const [expandedMenus, setExpandedMenus] = useState({});
  
  const toggleSubmenu = (path) => {
    setExpandedMenus(prev => ({ ...prev, [path]: !prev[path] }));
  };

  // Add Tenants and Audit Log links for super_admin only
  if (user?.role === 'super_admin') {
    navItems.splice(navItems.length - 1, 0, { 
      path: '/tenants', 
      label: 'Gestão de Tenants', 
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' 
    });
    navItems.splice(navItems.length - 1, 0, { 
      path: '/audit-log', 
      label: 'Log de Auditoria', 
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' 
    });
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/1ed57a29-834c-4440-94bb-8fa514a38873/images/893d9cb746463cc17aacfefa048bcc640ffd2f5ff4ab77b7e5239f606122dde9.png" 
                alt="CogniBiz Logo" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-lg font-['Plus_Jakarta_Sans']">CogniBiz</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <div key={item.path}>
                {item.submenu ? (
                  // Collapsible menu with submenu
                  <div>
                    <button
                      onClick={() => toggleSubmenu(item.path)}
                      className={`nav-link w-full justify-between ${location.pathname.startsWith(item.path) || location.pathname === '/graph' ? 'nav-link-active' : ''}`}
                      data-testid={`nav-${item.path.replace('/', '')}`}
                    >
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                        </svg>
                        {item.label}
                      </div>
                      <svg 
                        className={`w-4 h-4 transition-transform ${expandedMenus[item.path] ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedMenus[item.path] && (
                      <div className="ml-6 mt-1 space-y-1">
                        {item.submenu.map((subitem) => (
                          subitem.submenu ? (
                            // Nested collapsible submenu (e.g., Viaturas)
                            <div key={subitem.path}>
                              <button
                                onClick={() => toggleSubmenu(subitem.path)}
                                className={`nav-link text-sm py-2 w-full justify-between ${location.pathname.startsWith(subitem.path) ? 'nav-link-active' : ''}`}
                                data-testid={`nav-${subitem.path.replace(/\//g, '-')}-sub`}
                              >
                                <div className="flex items-center">
                                  <span className="w-2 h-2 rounded-full bg-current opacity-50 mr-2"></span>
                                  {subitem.label}
                                </div>
                                <svg 
                                  className={`w-3 h-3 transition-transform ${expandedMenus[subitem.path] ? 'rotate-180' : ''}`} 
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {expandedMenus[subitem.path] && (
                                <div className="ml-4 mt-1 space-y-1">
                                  {subitem.submenu.map((nestedItem) => (
                                    <Link
                                      key={nestedItem.path}
                                      to={nestedItem.path}
                                      className={`nav-link text-xs py-1.5 ${location.pathname === nestedItem.path ? 'nav-link-active' : ''}`}
                                      data-testid={`nav-${nestedItem.path.replace(/\//g, '-')}-nested`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40 mr-2"></span>
                                      {nestedItem.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            // Regular submenu item
                            <Link
                              key={subitem.path}
                              to={subitem.path}
                              className={`nav-link text-sm py-2 ${location.pathname === subitem.path ? 'nav-link-active' : ''}`}
                              data-testid={`nav-${subitem.path.replace('/', '')}-sub`}
                            >
                              <span className="w-2 h-2 rounded-full bg-current opacity-50 mr-2"></span>
                              {subitem.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Regular menu item
                  <Link
                    to={item.path}
                    className={`nav-link ${location.pathname === item.path ? 'nav-link-active' : ''}`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm font-medium">{user?.name?.charAt(0)}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Terminar Sessão
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 hover:bg-muted rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <div className="flex items-center gap-4 ml-auto">
            <ThemeToggle />
            
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 hover:bg-muted rounded-lg relative"
                data-testid="notifications-btn"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </button>
              
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-96 bg-card border border-border rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b border-border flex items-center justify-between">
                    <h3 className="font-semibold">Notificações</h3>
                    <div className="flex items-center gap-2">
                      {notifications.length > 0 && (
                        <>
                          <span className="text-xs text-muted-foreground">{notifications.length}</span>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await api.delete('/notifications');
                                fetchNotifications();
                                toast.success('Todas as notificações eliminadas');
                              } catch (error) {
                                toast.error('Erro ao eliminar notificações');
                              }
                            }}
                            className="text-xs text-red-500 hover:text-red-600 font-medium"
                            title="Limpar todas"
                          >
                            Limpar todas
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground text-center">Sem notificações</p>
                    ) : (
                      notifications.map((notif) => {
                        const getNotifIcon = () => {
                          if (notif.type === 'conflict_detected' || notif.type === 'conflict_scan') return '⚠️';
                          if (notif.type === 'analysis' || notif.type === 'conflict_scan') return '🔄';
                          if (notif.type === 'analysis_complete' || notif.type === 'conflict_scan_complete') return '✅';
                          if (notif.type === 'rgpc_violation') return '🚨';
                          if (notif.priority === 'high') return '🔴';
                          if (notif.priority === 'medium') return '🟡';
                          return '🔵';
                        };
                        const getPriorityClass = () => {
                          if (notif.priority === 'high' || notif.type === 'conflict_detected') return 'border-l-4 border-l-red-500';
                          if (notif.priority === 'medium') return 'border-l-4 border-l-amber-500';
                          return 'border-l-4 border-l-blue-500';
                        };
                        return (
                          <div 
                            key={notif.notification_id} 
                            className={`p-4 border-b border-border hover:bg-muted/50 ${getPriorityClass()} group`}
                          >
                            <div className="flex items-start gap-3">
                              <span className="text-lg">{getNotifIcon()}</span>
                              <div 
                                className="flex-1 min-w-0 cursor-pointer"
                                onClick={() => {
                                  if (notif.action_url) {
                                    window.location.href = notif.action_url;
                                  }
                                }}
                              >
                                <p className="text-sm font-medium">{notif.title}</p>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notif.message}</p>
                                <p className="text-xs text-muted-foreground/70 mt-1">
                                  {new Date(notif.created_at).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })}
                                </p>
                              </div>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await api.delete(`/notifications/${notif.notification_id}`);
                                    fetchNotifications();
                                  } catch (error) {
                                    toast.error('Erro ao eliminar notificação');
                                  }
                                }}
                                className="p-1 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Eliminar"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};


// Real-Time Alerts Panel Component
const RealTimeAlertsPanel = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetchAlerts();
    // Poll for new alerts every 30 seconds
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/alerts/realtime');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/dismiss`);
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      toast.success('Alerta arquivado');
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical_conflict':
        return (
          <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      case 'offer_limit_exceeded':
        return (
          <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'offer_auto_rejected':
        return (
          <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      critical: 'bg-red-500 text-white',
      high: 'bg-amber-500 text-white',
      medium: 'bg-yellow-400 text-black',
      low: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
    };
    return colors[priority] || colors.low;
  };

  const criticalCount = alerts.filter(a => a.priority === 'critical' || a.priority === 'high').length;

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="realtime-alerts-panel">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${criticalCount > 0 ? 'bg-red-100 dark:bg-red-900/30 animate-pulse' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
            <svg className={`w-5 h-5 ${criticalCount > 0 ? 'text-red-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              Alertas em Tempo Real
              {alerts.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-red-500 text-white">
                  {alerts.length}
                </span>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">
              {criticalCount > 0 
                ? `${criticalCount} alerta(s) crítico(s) requerem atenção` 
                : 'Monitorização de conflitos e ofertas'}
            </p>
          </div>
        </div>
        <svg 
          className={`w-5 h-5 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {expanded && (
        <div className="border-t border-border">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-emerald-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-muted-foreground">Sem alertas ativos</p>
              <p className="text-sm text-muted-foreground">O sistema está a monitorizar conflitos e ofertas</p>
            </div>
          ) : (
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {alerts.map((alert) => (
                <div 
                  key={alert.alert_id} 
                  className="p-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {getAlertIcon(alert.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(alert.priority)}`}>
                          {alert.priority === 'critical' ? 'CRÍTICO' : 
                           alert.priority === 'high' ? 'ALTO' : 
                           alert.priority === 'medium' ? 'MÉDIO' : 'BAIXO'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(alert.created_at).toLocaleString('pt-PT')}
                        </span>
                      </div>
                      <p className="font-medium text-sm">{alert.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {alert.entity_type && (
                          <button
                            onClick={() => navigate(`/${alert.entity_type}s/${alert.entity_id}`)}
                            className="text-xs text-primary hover:underline"
                          >
                            Ver {alert.entity_type === 'offer' ? 'oferta' : alert.entity_type === 'conflict' ? 'conflito' : 'entidade'}
                          </button>
                        )}
                        <button
                          onClick={() => dismissAlert(alert.alert_id)}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          Arquivar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="p-3 border-t border-border bg-muted/30">
            <button
              onClick={fetchAlerts}
              className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Atualizar Alertas
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Page
const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [offerAnalytics, setOfferAnalytics] = useState(null);
  const [entityStats, setEntityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, analyticsRes, entityRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/offer-analytics'),
          api.get('/dashboard/entity-stats')
        ]);
        setStats(statsRes.data);
        setOfferAnalytics(analyticsRes.data);
        setEntityStats(entityRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Erro ao carregar dados do dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const CHART_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6'];
  const TYPE_LABELS = { gift: 'Presente', invitation: 'Convite', benefit: 'Benefício', sponsorship: 'Patrocínio', other: 'Outro' };
  const SOURCE_LABELS = { client: 'Cliente', supplier: 'Fornecedor', other: 'Outro' };

  const handleCardClick = (type, data) => {
    setDetailModal({ type, data });
  };

  const navigateToEntity = (type, id) => {
    navigate(`/${type}/${id}`);
    setDetailModal(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Dashboard</h1>
        <p className="text-muted-foreground">Visão geral do estado de compliance</p>
      </div>

      {/* Stats Grid - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div 
          onClick={() => handleCardClick('clients', entityStats?.clients)}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
          data-testid="clients-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-blue-500 bg-blue-100 dark:bg-blue-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <span className="text-xs text-muted-foreground">Clique para detalhes</span>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.total_clients || 0}</p>
            <p className="text-sm text-muted-foreground">Total Clientes</p>
            {entityStats?.clients?.high_risk > 0 && (
              <p className="text-xs text-red-500 mt-1">{entityStats.clients.high_risk} alto risco</p>
            )}
          </div>
        </div>

        <div 
          onClick={() => handleCardClick('suppliers', entityStats?.suppliers)}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
          data-testid="suppliers-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-purple-500 bg-purple-100 dark:bg-purple-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className="text-xs text-muted-foreground">Clique para detalhes</span>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.total_suppliers || 0}</p>
            <p className="text-sm text-muted-foreground">Total Fornecedores</p>
            {entityStats?.suppliers?.high_risk > 0 && (
              <p className="text-xs text-red-500 mt-1">{entityStats.suppliers.high_risk} alto risco</p>
            )}
          </div>
        </div>

        <div 
          onClick={() => handleCardClick('employees', entityStats?.employees)}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
          data-testid="employees-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span className="text-xs text-muted-foreground">Clique para detalhes</span>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.total_employees || 0}</p>
            <p className="text-sm text-muted-foreground">Total Colaboradores</p>
          </div>
        </div>

        <div 
          onClick={() => navigate('/conflicts')}
          className={`stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all ${stats?.active_conflicts > 0 ? 'ring-2 ring-red-500' : ''}`}
          data-testid="conflicts-stat-card"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-red-500 bg-red-100 dark:bg-red-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.active_conflicts || 0}</p>
            <p className="text-sm text-muted-foreground">Conflitos Ativos</p>
          </div>
        </div>
      </div>

      {/* Offer Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Offer Trend Chart */}
        <div className="lg:col-span-2 rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans']">Tendência de Ofertas</h2>
              <p className="text-sm text-muted-foreground">Evolução das ofertas recebidas</p>
            </div>
            <div 
              onClick={() => navigate('/offers')}
              className="text-sm text-primary cursor-pointer hover:underline"
            >
              Ver todas →
            </div>
          </div>
          
          {offerAnalytics?.trend_data?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={offerAnalytics.trend_data}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => `Data: ${value}`}
                  formatter={(value, name) => {
                    const labels = { total: 'Total', value: 'Valor (€)', approved: 'Aprovadas', rejected: 'Rejeitadas', exceeds_limit: 'Excede Limite' };
                    return [value, labels[name] || name];
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} name="Total" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} name="Aprovadas" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={2} name="Rejeitadas" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="exceeds_limit" stroke="#f59e0b" strokeWidth={2} name="Excede Limite" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p>Sem dados de ofertas para mostrar</p>
            </div>
          )}
        </div>

        {/* Summary Stats - Clickable */}
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Resumo de Ofertas</h3>
            <div className="space-y-2">
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate('/offers')}
                data-testid="summary-total-offers"
              >
                <span className="text-sm">Total de Ofertas</span>
                <span className="font-bold">{offerAnalytics?.summary?.total_offers || 0}</span>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate('/offers')}
                data-testid="summary-total-value"
              >
                <span className="text-sm">Valor Total</span>
                <span className="font-bold text-emerald-500">{offerAnalytics?.summary?.total_value?.toLocaleString('pt-PT') || 0} €</span>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate('/offers')}
                data-testid="summary-avg-value"
              >
                <span className="text-sm">Valor Médio</span>
                <span className="font-bold">{offerAnalytics?.summary?.avg_value?.toFixed(2) || 0} €</span>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleCardClick('exceeds_limit', offerAnalytics?.exceeds_limit_offers)}
                data-testid="summary-exceeds-limit"
              >
                <span className="text-sm">Excedem Limite</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-orange-500">{offerAnalytics?.summary?.exceeds_limit_count || 0}</span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleCardClick('auto_rejected', offerAnalytics?.auto_rejected_offers)}
                data-testid="summary-auto-rejected"
              >
                <span className="text-sm">Auto-Rejeitadas</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-500">{offerAnalytics?.summary?.auto_rejected_count || 0}</span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
              <div 
                className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => handleCardClick('pending', offerAnalytics?.pending_offers)}
                data-testid="summary-pending"
              >
                <span className="text-sm">Pendentes de Aprovação</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-amber-500">{offerAnalytics?.summary?.pending_count || 0}</span>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Top Offerers */}
          <div className="rounded-xl border bg-card p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Principais Ofertantes</h3>
            <div className="space-y-2">
              {offerAnalytics?.top_offerers?.slice(0, 5).map((offerer, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleCardClick('offerer', offerer)}
                  data-testid={`top-offerer-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      offerer.type === 'client' ? 'bg-blue-500' : 
                      offerer.type === 'supplier' ? 'bg-purple-500' : 'bg-gray-500'
                    }`}></span>
                    <span className="text-sm truncate max-w-32">{offerer.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{offerer.value?.toLocaleString('pt-PT')} €</span>
                    <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))}
              {(!offerAnalytics?.top_offerers || offerAnalytics.top_offerers.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-2">Sem dados</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Offers by Type and Source Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Type */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4">Ofertas por Tipo</h2>
          {offerAnalytics?.by_type?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={offerAnalytics.by_type}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="type" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => TYPE_LABELS[value] || value}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => TYPE_LABELS[value] || value}
                />
                <Bar dataKey="count" fill="#3b82f6" name="Quantidade" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p>Sem dados</p>
            </div>
          )}
        </div>

        {/* By Source */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4">Ofertas por Origem</h2>
          {offerAnalytics?.by_source?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={offerAnalytics.by_source}
                  dataKey="value"
                  nameKey="source"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ source, percent }) => `${SOURCE_LABELS[source] || source}: ${(percent * 100).toFixed(0)}%`}
                >
                  {offerAnalytics.by_source.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [`${value.toLocaleString('pt-PT')} €`, SOURCE_LABELS[name] || name]}
                />
                <Legend formatter={(value) => SOURCE_LABELS[value] || value} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              <p>Sem dados</p>
            </div>
          )}
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div 
          onClick={() => navigate('/offers')}
          className="stat-card cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="p-3 rounded-lg text-amber-500 bg-amber-100 dark:bg-amber-900/30">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{stats?.pending_offers || 0}</p>
            <p className="text-sm text-muted-foreground">Ofertas Pendentes</p>
          </div>
        </div>
        <StatCard
          title="Entidades Alto Risco"
          value={stats?.high_risk_entities || 0}
          icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          color="red"
        />
        <StatCard
          title="Documentos a Expirar"
          value={stats?.expiring_documents || 0}
          icon="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          color="amber"
        />
      </div>

      {/* RGPC Compliance & Risk Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* RGPC Compliance */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Conformidade RGPC
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                <p className="text-2xl font-bold text-emerald-600">{stats?.rgpc_compliant_offers || 0}</p>
                <p className="text-xs text-muted-foreground">Conformes</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-2xl font-bold text-red-600">{stats?.rgpc_non_compliant_offers || 0}</p>
                <p className="text-xs text-muted-foreground">Não Conformes</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                <p className="text-2xl font-bold text-gray-600">{stats?.rgpc_pending_analysis || 0}</p>
                <p className="text-xs text-muted-foreground">Pendentes</p>
              </div>
            </div>
            {(stats?.rgpc_compliant_offers > 0 || stats?.rgpc_non_compliant_offers > 0) && (
              <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                <div 
                  className="bg-emerald-500 h-full" 
                  style={{ 
                    width: `${((stats?.rgpc_compliant_offers || 0) / ((stats?.rgpc_compliant_offers || 0) + (stats?.rgpc_non_compliant_offers || 0))) * 100}%` 
                  }}
                ></div>
                <div 
                  className="bg-red-500 h-full" 
                  style={{ 
                    width: `${((stats?.rgpc_non_compliant_offers || 0) / ((stats?.rgpc_compliant_offers || 0) + (stats?.rgpc_non_compliant_offers || 0))) * 100}%` 
                  }}
                ></div>
              </div>
            )}
            <button
              onClick={() => navigate('/offers')}
              className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors"
            >
              Ver Ofertas →
            </button>
          </div>
        </div>

        {/* NIF Cross-Reference & Risk Alerts */}
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Alertas de Risco
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                </svg>
                <span className="font-medium">Conflitos por NIF</span>
              </div>
              <span className="text-xl font-bold text-amber-600">{stats?.nif_based_conflicts || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Riscos de Ofertas</span>
              </div>
              <span className="text-xl font-bold text-red-600">{stats?.offer_risk_conflicts || 0}</span>
            </div>
            {(stats?.nif_based_conflicts > 0 || stats?.offer_risk_conflicts > 0) && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                Clique em Conflitos para ver detalhes e visualização em grafo
              </p>
            )}
            <button
              onClick={() => navigate('/conflicts')}
              className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors"
            >
              Ver Conflitos →
            </button>
          </div>
        </div>
      </div>

      {/* Recent Conflicts */}
      <div className="rounded-xl border bg-card p-6">
        <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-4">Conflitos Recentes</h2>
        {stats?.recent_conflicts?.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Nenhum conflito detetado</p>
        ) : (
          <div className="space-y-3">
            {stats?.recent_conflicts?.map((conflict) => (
              <div 
                key={conflict.conflict_id} 
                className="flex items-center justify-between p-4 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                onClick={() => navigate('/conflicts')}
              >
                <div>
                  <p className="font-medium">{conflict.entity1_name} ↔ {conflict.entity2_name}</p>
                  <p className="text-sm text-muted-foreground">{conflict.relationship}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  conflict.severity === 'high' || conflict.severity === 'critical' ? 'risk-badge-high' :
                  conflict.severity === 'medium' ? 'risk-badge-medium' : 'risk-badge-low'
                }`}>
                  {translateRisk(conflict.severity)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Real-Time Alerts Panel */}
      <RealTimeAlertsPanel />

      {/* Detail Modal */}
      {detailModal && (
        <DashboardDetailModal
          type={detailModal.type}
          data={detailModal.data}
          onClose={() => setDetailModal(null)}
          onNavigate={navigateToEntity}
        />
      )}
    </div>
  );
};

// Dashboard Detail Modal
const DashboardDetailModal = ({ type, data, onClose, onNavigate }) => {
  const navigate = useNavigate();
  const typeLabels = {
    clients: 'Clientes',
    suppliers: 'Fornecedores',
    employees: 'Colaboradores',
    offerer: 'Ofertante',
    exceeds_limit: 'Ofertas que Excedem Limite',
    auto_rejected: 'Ofertas Auto-Rejeitadas',
    pending: 'Ofertas Pendentes de Aprovação'
  };

  const statusLabels = {
    pending: 'Pendente',
    approved: 'Aprovada',
    rejected: 'Rejeitada',
    auto_rejected: 'Auto-Rejeitada',
    ipss_donated: 'Doada IPSS',
    lottery: 'Sorteio'
  };

  const isOfferList = ['exceeds_limit', 'auto_rejected', 'pending'].includes(type);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold font-['Plus_Jakarta_Sans']">
            {typeLabels[type] || type}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {isOfferList ? (
            <>
              {(!data || data.length === 0) ? (
                <p className="text-center text-muted-foreground py-8">Nenhuma oferta encontrada</p>
              ) : (
                <div className="space-y-3">
                  {data.map((offer, idx) => (
                    <div
                      key={offer.offer_id || idx}
                      className="p-4 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => { navigate('/offers'); onClose(); }}
                      data-testid={`offer-detail-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{offer.employee_name}</p>
                          <p className="text-sm text-muted-foreground">Oferecido por: {offer.offered_by}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{offer.value?.toLocaleString('pt-PT')} €</p>
                          {offer.limit_exceeded_amount > 0 && (
                            <p className="text-xs text-red-500">+{offer.limit_exceeded_amount?.toFixed(2)}€ acima</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{offer.offer_date}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          offer.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30' :
                          offer.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' :
                          'bg-red-100 text-red-700 dark:bg-red-900/30'
                        }`}>
                          {statusLabels[offer.status] || offer.status}
                        </span>
                      </div>
                      {offer.exceeds_limit && (
                        <div className="mt-2 text-xs text-orange-500 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Excede limite da política
                        </div>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => { navigate('/offers'); onClose(); }}
                    className="w-full py-2 text-sm text-primary hover:bg-muted rounded-lg transition-colors mt-4"
                  >
                    Ver todas as ofertas →
                  </button>
                </div>
              )}
            </>
          ) : type === 'offerer' ? (
            <>
              <div className="text-center pb-4 border-b border-border">
                <h3 className="text-lg font-bold">{data?.name}</h3>
                <p className="text-sm text-muted-foreground capitalize">{data?.type === 'client' ? 'Cliente' : data?.type === 'supplier' ? 'Fornecedor' : 'Outro'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">{data?.count || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Ofertas</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold text-emerald-500">{data?.value?.toLocaleString('pt-PT') || 0} €</p>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Risk Distribution */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-center">
                  <p className="text-xl font-bold text-red-500">{data?.high_risk || 0}</p>
                  <p className="text-xs text-muted-foreground">Alto Risco</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-center">
                  <p className="text-xl font-bold text-amber-500">{data?.medium_risk || 0}</p>
                  <p className="text-xs text-muted-foreground">Médio Risco</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-center">
                  <p className="text-xl font-bold text-emerald-500">{data?.low_risk || 0}</p>
                  <p className="text-xs text-muted-foreground">Baixo Risco</p>
                </div>
              </div>

              {/* Status Distribution */}
              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-medium mb-3">Por Status</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="font-bold">{data?.by_status?.pending || 0}</p>
                    <p className="text-xs text-muted-foreground">Pendente</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="font-bold text-emerald-500">{data?.by_status?.approved || 0}</p>
                    <p className="text-xs text-muted-foreground">Aprovado</p>
                  </div>
                  <div className="p-2 rounded-lg bg-muted/50 text-center">
                    <p className="font-bold text-red-500">{data?.by_status?.rejected || 0}</p>
                    <p className="text-xs text-muted-foreground">Rejeitado</p>
                  </div>
                </div>
              </div>

              {/* High Risk Entities Preview */}
              {data?.high_risk_entities?.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-medium mb-3">Entidades de Alto Risco</h4>
                  <div className="space-y-2">
                    {data.high_risk_entities.map((entity, idx) => {
                      const entityType = type.slice(0, -1); // clients -> client
                      const idKey = `${entityType}_id`;
                      return (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                          onClick={() => onNavigate(type, entity[idKey])}
                        >
                          <div>
                            <p className="font-medium">{entity.name}</p>
                            <p className="text-xs text-muted-foreground">Score: {Math.round((entity.risk_score || 0) * 100)}</p>
                          </div>
                          <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Navigate Button */}
          {type !== 'offerer' && (
            <button
              onClick={() => onNavigate(type, '')}
              className="w-full mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
            >
              Ver Todos os {typeLabels[type]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, highlight }) => {
  const colorClasses = {
    blue: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
    purple: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
    green: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30',
    red: 'text-red-500 bg-red-100 dark:bg-red-900/30',
    amber: 'text-amber-500 bg-amber-100 dark:bg-amber-900/30',
  };

  return (
    <div className={`stat-card ${highlight ? 'ring-2 ring-red-500' : ''}`}>
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
          </svg>
        </div>
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold font-['Plus_Jakarta_Sans']">{value}</p>
        <p className="text-sm text-muted-foreground">{title}</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
